/**
 * Paramate SDK error interception — constructor patch + error classification.
 *
 * The Paramate SDK dispatches a global `paramate-configurator-error` window
 * event on every failed request (see doRequest() in
 * public/lib-custom/paramateConfigurator.js), regardless of which
 * `Paramate.Configurator` instance failed or who constructed it — as long as
 * `debug.on` is truthy (true by default). as-configurator.ts already listens
 * for that event for classification/UI. What it can't reach without help is
 * `debug.error` itself: it's an instance property assigned inside the SDK's
 * constructor body, not the prototype, so the only way to override it for
 * configurators an app constructs directly (not via AsConfigurator.init()) is
 * to wrap the constructor function itself. That's what installParamateConstructorPatch
 * does — it suppresses the SDK's own alert()-based default debug.error (while
 * still invoking one an app supplied, for back-compat) and forces debug.on so
 * the window event always fires.
 */

const PATCH_MARKER = '__asPatchedConfigurator';

export function installParamateConstructorPatch(): void {
  const Paramate = (window as any).Paramate;
  if (!Paramate?.Configurator || Paramate.Configurator[PATCH_MARKER]) return;
  const Original = Paramate.Configurator;

  function Patched(this: any, opts: any) {
    const appErrorFn = typeof opts?.debug?.error === 'function';
    const instance = new Original(opts);
    const originalDebugError = instance.debug.error;
    // Always let the request-failure pipeline (log/error/window-event) run,
    // even if the caller passed debug: { on: false } — classification relies
    // on the window event.
    instance.debug.on = true;
    instance.debug.error = function (errObj: any) {
      // Legacy escape hatch: still call an app-supplied debug.error for its
      // own side effects, but never the SDK's own alert()-based default.
      if (appErrorFn) {
        try { originalDebugError.call(window, errObj); } catch (e) { console.error('[paramate-error-interceptor] app debug.error threw', e); }
      }
    };
    return instance;
  }

  Patched.prototype = Original.prototype;
  Patched[PATCH_MARKER] = true;
  Paramate.Configurator = Patched;
}

// Eager, top-level side effect so any `new Paramate.Configurator(...)` — ours
// or an app's own — picks up the patched constructor, including configurators
// built before <as-configurator> ever binds one. Idempotent (guarded by
// PATCH_MARKER), safe to import/call more than once.
installParamateConstructorPatch();

// ============================================================================
// Error classification
// ============================================================================

export interface ParamateSdkError {
  status: number;
  statusText?: string;
  response?: { message?: string; info?: string; [k: string]: any };
}

export type ParamateErrorCase =
  | 'service-interrupted'
  | 'unsupported-file-format'
  | 'file-too-complex'
  | 'save-operation-timeout'
  | 'operation-timeout'
  | 'unexpected-error';

export interface ParamateErrorClassification {
  case: ParamateErrorCase;
  titleKey: string;
  messageKey: string;
  defaultTitle: string;
  defaultMessage: string;
  err: ParamateSdkError;
  /** True when this case should also be logged to the server (unexpected-error). */
  sendLog?: boolean;
}

export type ParamateClassification =
  | ParamateErrorClassification
  /** 404/401 — caller should defer to the existing session-expiry flow. */
  | { case: 'session-expired' }
  /** 499/aborted (cancelLastRequest()) — not a real error, no UI. */
  | { case: 'ignore' };

const CASE_COPY: Record<ParamateErrorCase, { titleKey: string; messageKey: string; defaultTitle: string; defaultMessage: string }> = {
  'service-interrupted': {
    titleKey: 'paramateError.serviceInterrupted.title',
    messageKey: 'paramateError.serviceInterrupted.message',
    defaultTitle: 'Service Interrupted',
    defaultMessage: 'A service was interrupted due to a loss of internet connection. Please check your connection and try again.',
  },
  'unsupported-file-format': {
    titleKey: 'paramateError.unsupportedFileFormat.title',
    messageKey: 'paramateError.unsupportedFileFormat.message',
    defaultTitle: 'Unsupported File Format',
    defaultMessage: 'The uploaded file format is not supported. Please upload a file in a supported format.',
  },
  'file-too-complex': {
    titleKey: 'paramateError.fileTooComplex.title',
    messageKey: 'paramateError.fileTooComplex.message',
    defaultTitle: 'File Too Complex to Process',
    defaultMessage: 'The uploaded file seems to be too complex to process. STEP/3MF files can expand significantly upon conversion and may exceed the supported limit. Please suppress any unwanted geometry in your CAD software, export the file as an STL, and upload it again. The recommended file size is under 10 MB.',
  },
  'save-operation-timeout': {
    titleKey: 'paramateError.saveOperationTimeout.title',
    messageKey: 'paramateError.saveOperationTimeout.message',
    defaultTitle: 'Save Operation Timeout',
    defaultMessage: 'The file could not be saved. The operation exceeded the expected time limit. If you have STEP export enabled, switching to STL may help. Reducing the export scope by selecting only the models you need may also speed things up. Please try again.',
  },
  'operation-timeout': {
    titleKey: 'paramateError.operationTimeout.title',
    messageKey: 'paramateError.operationTimeout.message',
    defaultTitle: 'Operation Timeout',
    defaultMessage: 'The current operation could not be completed. The process exceeded the expected time limit.',
  },
  'unexpected-error': {
    titleKey: 'paramateError.unexpectedError.title',
    messageKey: 'paramateError.unexpectedError.message',
    defaultTitle: 'Unexpected Error',
    defaultMessage: 'An unexpected error occurred on the server. Please try again later.',
  },
};

function classified(c: ParamateErrorCase, err: ParamateSdkError, sendLog = false): ParamateErrorClassification {
  return { case: c, err, sendLog, ...CASE_COPY[c] };
}

/**
 * Classifies a raw Paramate SDK error (the {status, statusText, response}
 * shape passed to debug.error / carried by the paramate-configurator-error
 * window event) into one of the known UI cases.
 *
 * Mirrors the legacy per-app handleError()/apiError()/cadError() split:
 * no response at all → network/connection issue; HTTP 500 → trcad/CAD
 * execution error (further split via the trcad log, when a session exists,
 * into memory/timeout sub-cases); everything else → surfaced from the
 * server's own response message where present.
 */
export async function classifyParamateError(
  err: ParamateSdkError,
  opts: { getLog?: () => Promise<{ error?: string; message?: string }>; hasActiveSession: boolean; saveInFlight: boolean },
): Promise<ParamateClassification> {
  if (err.status === 499 && err.statusText === 'aborted') return { case: 'ignore' };
  if (!err.response) return classified('service-interrupted', err);
  if (err.status === 404 || err.status === 401) return { case: 'session-expired' };
  if (err.status === 415) return classified('unsupported-file-format', err);
  if (err.status === 413) return classified('file-too-complex', err);

  if (err.status === 500) {
    // No trcad log available before a session exists (mirrors the legacy
    // cadError()'s `configSession != ''` guard) — nothing to sub-classify.
    if (!opts.hasActiveSession || !opts.getLog) return classified('unexpected-error', err, true);
    try {
      const sLog = await opts.getLog();
      const logError = (sLog?.error ?? '').toLowerCase();
      if (logError.includes('memory')) return classified('file-too-complex', err);
      if (logError.includes('timeout')) return classified(opts.saveInFlight ? 'save-operation-timeout' : 'operation-timeout', err);
      return classified('unexpected-error', err, true);
    } catch {
      // A failed log fetch must not mask the original error.
      return classified('unexpected-error', err, true);
    }
  }

  // Other 4xx/5xx — no dedicated case in the ticket's table; surface as
  // "unexpected error" but the caller can still read err.response.message/info.
  return classified('unexpected-error', err, true);
}
